/** SignedOut: the whole page when nobody is signed in, with nothing of the association on it: the sign-in form. Signing in asks everything on screen again, so the page that was asked for comes back at the same address (#704: the classic `/login` is gone). */
import { useRef, useState, type FormEvent } from "react";
import { login, type ApiError } from "../../api/client";
import { invalidate } from "../data/useQuery";
import { Button, Card, CardBody, Field, Input, Mark } from "../kit";

export function SignedOut() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<"username" | "password" | null>(null);
  const usernameField = useRef<HTMLInputElement>(null);
  const passwordField = useRef<HTMLInputElement>(null);

  const signIn = async (event: FormEvent) => {
    event.preventDefault();
    if (sending) return;
    // The button is never greyed out: an empty field is named and focused instead.
    const empty = !username.trim() ? "username" : !password ? "password" : null;
    setMissing(empty);
    if (empty) {
      (empty === "username" ? usernameField : passwordField).current?.focus();
      return;
    }
    setSending(true);
    setError(null);
    try {
      await login(username.trim(), password);
      invalidate();
    } catch (err) {
      const status = (err as ApiError).status;
      setError(status === 400 || status === 401 || status === 403 ? "Wrong username or password." : "Could not sign in. Try again.");
      setSending(false);
    }
  };

  return (
    <main className="flex min-h-dvh w-full items-center justify-center bg-background px-4 py-10">
      <div className="flex w-full max-w-[380px] animate-rise flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-4">
          <span className="flex size-12 items-center justify-center rounded-xl bg-brand-tint-strong">
            <Mark />
          </span>
          <h1 className="text-display font-medium text-foreground">Sign in to Keystone</h1>
        </div>
        <Card className="w-full">
          <CardBody>
            <form onSubmit={(event) => void signIn(event)} noValidate className="flex w-full flex-col gap-4 p-1">
              <Field label="Username" error={missing === "username" ? "Enter your username." : undefined}>
                <Input ref={usernameField} name="username" autoComplete="username"value={username} onChange={(event) => setUsername(event.target.value)} aria-invalid={error || missing === "username" ? true : undefined} />
              </Field>
              <Field label="Password" error={error ?? (missing === "password" ? "Enter your password." : undefined)}>
                <Input ref={passwordField} name="password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} aria-invalid={error || missing === "password" ? true : undefined} />
              </Field>
              <Button type="submit" variant="primary" className="mt-1 h-10 w-full justify-center" aria-busy={sending}>
                {sending ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </main>
  );
}
